import { ErrorMessage } from "@hookform/error-message";
import AsyncSelect from "react-select/async";
import { useDebouncedCallback } from "use-debounce";
import { Maybe } from "../../utils/types/maybe.ts";
import ErrorLabel from "./errorLabel.tsx";

type Props<T> = {
  name: string;
  label: string;
  placeholder?: Maybe<string>;
  initialValue?: Maybe<T>;
  queryFn: (nameFilter: string) => Promise<T[]>;
  renderLabel?: (value: T) => string;
  onChange: (value: Maybe<T>) => void;
};

export default function EntityAsyncSelect<T>({
  name,
  label,
  placeholder,
  initialValue,
  queryFn,
  renderLabel,
  onChange,
}: Props<T>) {
  const debouncedQueryFn = useDebouncedCallback(
    (inputValue: string, callback) => {
      queryFn(inputValue)
        .then((results) => callback(results ?? []))
        .catch((error) => {
          console.error(error);
          callback([]);
        });
    },
    250,
  );

  return (
    <div className="sm:col-span-4">
      <label
        htmlFor={name}
        className="block text-sm font-medium leading-6 text-gray-900"
      >
        {label}
      </label>
      <div className="mt-1 flex gap-2 border-0 bg-transparent py-1.5">
        <AsyncSelect
          className="!text-gray-900 sm:text-sm sm:leading-6 flex-grow"
          classNames={{
            control: (state) =>
              state.isFocused
                ? "!border-sky-600 !shadow-sm"
                : "border-grey-300",
            option: () => "!text-gray-900",
          }}
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary: "#bae6fd",
            },
          })}
          placeholder={placeholder ?? "Search..."}
          noOptionsMessage={() => "Please provide a search term"}
          getOptionLabel={renderLabel ?? undefined}
          cacheOptions
          defaultOptions
          loadOptions={debouncedQueryFn}
          onChange={onChange}
          value={initialValue}
        />
        <div className="flex-none p-1.5">
          <div className="w-5" />
        </div>
      </div>
      <ErrorMessage
        name={name}
        render={({ message }) => <ErrorLabel message={message} />}
      />
    </div>
  );
}
